<template>
  <div :class="['row', `obsform__step${step + 1}`]">
    <div class="col-12 pr-0 pl-0">
      <Mapbox
        :styles="{ 'map-wrapper--strip': step !== 0, 'map-wrapper--absolutestrip': step === 0, 'map-wrapper--mobile': true }"
        :hidden="false"
        :closable="false"
        :expanded="true"
        :initialBounds="[[150.95497440765524, -48.16661474807524],[192.9941249721271, -33.658188780764924]]"
        :options="{ zoom: 6 }"
        :onLoad="handleMapLoad"
        :onResize="updateObservationLocation"
        :onDragEnd="updateObservationLocation"
        :onZoomEnd="updateObservationLocation">
        <div slot="extra">
          <PublicMarker :icon="'observation-select'" />
        </div>
      </Mapbox>
    </div>
    <div :class="['col-12', 'obsform', {'obsform--submitting': submitting}]">
      <Loader v-if="submitting" />
      <div class="container mt-2" v-show="!submitting">
        <div class="row mt-4">
          <div class="col-12 typography">
            <div :class="['row', 'mt-4', {'hidden-sm-down': step === 0}]">
              <div class="col-12">
                <h1>Submit a Public Observation</h1>
                <p><strong>Step {{ step + 1 }}</strong> of 5</p>
              </div>
            </div>

            <section v-if="step === 0">
              <div class="row row--primary">
                <div class="col-12">
                  <h2>Where is your public observation?</h2>
                  <p>Please zoom and pan the map above so that the pin is as close to your location as possible.</p>
                  <div class="row ml-0">
                      <figure class="col-auto">
                          <img src="/resources/themes/default/images/avalanche-warning.svg" alt="Warning" />
                      </figure>
                      <div class="col col-lg-8">
                          <p><strong>For avalanche observations</strong>, please locate the pin as close as possible to where the avalanche occurred/was triggered, NOT where the avalanche was witnessed from.</p>
                      </div>
                  </div>
                </div>
              </div>
            </section>

            <section v-if="step === 1">
              <multi-choice
                key="type"
                v-model="formData.type"
                label="What have you observed?"
                :primary="true"
                :options="[
                  { value:'avalanche', label:'An avalanche' },
                  { value:'snowpack', label:'Snowpack conditions' },
                ]" />
              <div class="row" v-if="formData.type">
                <div class="col-12">
                  <hr>
                  <label for="formData-name">Provide a brief description / observation title</label>
                  <input type="text" id="formData-name" v-model="formData.name" maxlength="200" required>
                </div>
              </div>
              <div v-if="formData.type === 'avalanche'">
                <multi-choice
                  key="isPeopleInvolved"
                  v-model="formData.avalanche.isPeopleInvolved"
                  label="Did this avalanche involve people?"
                  type="boolean"
                  :options="[
                  { value:true, label:'Yes' },
                  { value:false, label:'No' },
                ]" />
                <multi-choice
                  key="isStructuresInvolved"
                  v-model="formData.avalanche.isStructuresInvolved"
                  label="Did this avalanche involve structures?"
                  type="boolean"
                  :options="[
                  { value:true, label:'Yes' },
                  { value:false, label:'No' },
                ]" />
              </div>
            </section>

            <section v-if="step === 2 && formData.type === 'avalanche'">
              <multi-choice
                key="isWitnessed"
                v-model="formData.avalanche.isWitnessed"
                label="Did you witness/experience this avalanche?"
                type="boolean"
                :primary="true"
                :options="[
                  { value:true, label:'Yes, I witnessed or experienced this avalanche' },
                  { value:false, label:'No, I encountered an old avalanche' },
                ]" />
              <div class="row" v-if="formData.avalanche.isWitnessed === true">
                <div class="col-12">
                  <hr>
                  <label>When did the avalanche occur?</label>
                  <datetime v-if="formData.avalanche.isWitnessed"
                            type="datetime"
                            v-model="formData.time"
                            :maxDate="maxDatetime"
                            monday-first
                            auto-continue
                            auto-close
                            required />
                </div>
              </div>
              <div v-if="formData.avalanche.isWitnessed === false">
                <div class="row">
                  <div class="col-12">
                    <hr>
                    <label>When did you observe this avalanche?</label>
                    <datetime type="datetime"
                              v-model="formData.time"
                              :maxDate="maxDatetime"
                              monday-first
                              auto-continue
                              auto-close
                              required />
                  </div>
                </div>
                <multi-choice
                  key="estimatedTime"
                  v-model="formData.avalanche.estimatedTime"
                  label="When do you estimate the avalanche occurred?"
                  :options="cms.publicObsOptions.TriggerTimeEstimated" />
              </div>
              <div v-if="formData.avalanche.isWitnessed !== null">
                <multi-choice
                  key="triggerType"
                  v-model="formData.avalanche.triggerType"
                  label="Trigger"
                  :other="true"
                  :options="triggerTypes" />
                <div class="row">
                  <div class="col-12">
                    <label for="formData-avalanche-measurement-length">Length (m)</label>
                    <input type="number"
                           id="formData-avalanche-measurement-length"
                           min="0"
                           v-model="formData.avalanche.measurement.length">
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <label for="formData-avalanche-measurement-width">Width (m)</label>
                    <input type="number"
                           id="formData-avalanche-measurement-width"
                           min="0"
                           v-model="formData.avalanche.measurement.width">
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <label for="formData-avalanche-measurement-depth">Depth (cm)</label>
                    <input type="number"
                           id="formData-avalanche-measurement-depth"
                           min="0"
                           v-model="formData.avalanche.measurement.depth">
                  </div>
                </div>
                <div class="row obsform__advanced">
                  <div class="col-12">
                    <div class="row mb-0">
                      <div class="col-8">
                        <h3>Add extra observation information (optional)</h3>
                        <p class="mb-0" v-if="!isAdvanced">Expand to add advanced details such as character, aspect and slope angle.</p>
                      </div>
                      <div class="col-4 text-right">
                        <button v-if="!isAdvanced" class="button button--link button--open" @click="isAdvanced = true">Expand</button>
                        <button v-if="isAdvanced" class="button button--link button--close" @click="isAdvanced = false">Hide</button>
                      </div>
                    </div>

                    <div v-if="isAdvanced">
                      <div class="row">
                        <div class="col-12">
                          <label>Size</label>
                          <select id="formData-avalanche-size" class="custom-select" v-model="formData.avalanche.size">
                            <option value=""></option>
                            <option v-for="n in 10" :value="n * 0.5">{{ n * 0.5 }}</option>
                          </select>
                        </div>
                      </div>
                      <div class="row">
                        <div class="col-12">
                          <label for="formData-avalanche-character">Character</label>
                          <select id="formData-avalanche-character" class="custom-select" v-model="formData.avalanche.character">
                            <option value=""></option>
                            <option v-for="character in characterOptions" :key="character.value" :value="character.value">{{ character.label }}</option>
                          </select>
                        </div>
                      </div>
                      <div class="row">
                        <div class="col-12">
                          <label for="formData-avalanche-aspect">Aspect</label>
                          <select id="formData-avalanche-aspect" class="custom-select" v-model="formData.avalanche.aspect">
                            <option value=""></option>
                            <option v-for="aspect in aspects" :value="aspect">{{ aspect }}</option>
                          </select>
                        </div>
                      </div>
                      <div class="row">
                        <div class="col-12">
                          <label for="formData-avalanche-elevation">Elevation (m)</label>
                          <input type="number"
                                 id="formData-avalanche-elevation"
                                 min="0"
                                 v-model="formData.avalanche.elevation">
                        </div>
                      </div>
                      <div class="row">
                        <div class="col-12">
                          <label>Slope angle (&deg;)</label>
                          <select id="formData-avalanche-slopeAngle" class="custom-select" v-model="formData.avalanche.slopeAngle">
                            <option value=""></option>
                            <option v-for="n in 10" :value="10 + (n * 5)">{{ 10 + (n * 5) }}</option>
                          </select>
                        </div>
                      </div>
                    </div><!-- /isAdvanced -->
                  </div>
                </div>
              </div>
            </section>

            <section v-if="step === 2 && formData.type === 'snowpack'">
              <div class="row">
                <div class="col-12">
                  <label>When did you observe this snowpack?</label>
                  <datetime type="datetime"
                            v-model="formData.time"
                            :maxDate="maxDatetime"
                            monday-first
                            auto-continue
                            auto-close
                            required />
                </div>
              </div>
              <div class="row">
                <div class="col-12">
                  <label for="formData-snowpack-aspect">Aspect</label>
                  <select id="formData-snowpack-aspect" class="custom-select" v-model="formData.snowpack.aspect">
                    <option value=""></option>
                    <option v-for="aspect in aspects" :value="aspect">{{ aspect }}</option>
                  </select>
                </div>
              </div>
              <div class="row">
                <div class="col-12">
                  <label for="formData-snowpack-elevation">Elevation (m)</label>
                  <input type="number" id="formData-snowpack-elevation" v-model="formData.snowpack.elevation">
                </div>
              </div>
              <div class="row">
                <div class="col-12">
                  <label for="formData-snowpack-depth">Snowpack depth (cm)</label>
                  <input type="number" id="formData-snowpack-depth" v-model="formData.snowpack.depth">
                </div>
              </div>
              <multi-choice
                key="surfaceConditions"
                v-model="formData.snowpack.surfaceConditions"
                label="Surface conditions"
                :options="cms.publicObsOptions.SurfaceCondition" />
              <multi-choice
                key="footOrSkiPenetration"
                v-model="formData.snowpack.footOrSkiPenetration"
                label="Foot or ski penetration?"
                :options="[
                  { value:'foot', label:'Foot' },
                  { value:'ski', label:'Ski' }
                ]" />
              <div class="row">
                <div class="col-12" v-if="formData.snowpack.footOrSkiPenetration === 'foot'">
                  <label for="formData-snowpack-footPenetration">Penetration depth (cm)</label>
                  <input type="number" id="formData-snowpack-footPenetration" v-model="formData.snowpack.footPenetration">
                </div>
                <div class="col-12" v-if="formData.snowpack.footOrSkiPenetration === 'ski'">
                  <label for="formData-snowpack-skiPenetration">Penetration depth (cm)</label>
                  <input type="number" id="formData-snowpack-skiPenetration" v-model="formData.snowpack.skiPenetration">
                </div>
              </div>
              <multi-choice
                key="testResult"
                v-model="formData.snowpack.testResult"
                label="Test result"
                :options="cms.publicObsOptions.TestResult" />
              <multi-choice
                key="fractureCharacter"
                v-model="formData.snowpack.fractureCharacter"
                label="Test fracture character"
                :options="cms.publicObsOptions.FractureCharacter" />
              <div class="row">
                <div class="col-12">
                  <label for="formData-snowpack-fractureDepth">Fracture depth (cm)</label>
                  <input type="number" id="formData-snowpack-fractureDepth" v-model="formData.snowpack.fractureDepth">
                </div>
              </div>
            </section>

            <section v-if="step === 3">
              <div v-if="formData.avalanche.isPeopleInvolved">
                <multi-choice
                  key="activityType"
                  v-model="formData.avalanche.peopleInvolved.activityType"
                  label="Activity type at time of avalanche"
                  :other="true"
                  :options="cms.publicObsOptions.ActivityType" />
                <div class="row">
                  <div class="col-12">
                    <label for="formData-avalanche-peopleInvolved-groupSize">Total group size</label>
                    <input type="number"
                           id="formData-avalanche-peopleInvolved-groupSize"
                           min="0"
                           v-model="formData.avalanche.peopleInvolved.groupSize">
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <label for="formData-avalanche-peopleInvolved-peopleCaught">Total people caught</label>
                    <input type="number"
                           id="formData-avalanche-peopleInvolved-peopleCaught"
                           min="0"
                           v-model="formData.avalanche.peopleInvolved.peopleCaught">
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <label for="formData-avalanche-peopleInvolved-peopleInjured">Total people injured</label>
                    <input type="number"
                           id="formData-avalanche-peopleInvolved-peopleInjured"
                           min="0"
                           v-model="formData.avalanche.peopleInvolved.peopleInjured">
                  </div>
                </div>
              </div>
              <div v-if="formData.avalanche.isStructuresInvolved" class="row">
                <div class="col-12">
                  <label for="formData-avalanche-structuresInvolved">Structures involved</label>
                  <textarea id="formData-avalanche-structuresInvolved" v-model="formData.avalanche.structuresInvolved"></textarea>
                </div>
              </div>
              <multi-choice
                key="isPublic"
                v-model="formData.avalanche.isPublic"
                label="Make this a public observation?"
                type="boolean"
                :options="[
                  { value: true, label: 'Yes - will be publicly available for 7 days' },
                  { value: false, label: 'No - submit your observation only to the Mountain Safety Council' },
                ]" />
            </section>

            <section v-if="step === 4">
              <div class="row">
                <div class="col-12">
                  <label for="formData-comment">Any further comments?</label>
                  <textarea id="formData-comment" v-model="formData.comment"></textarea>
                </div>
              </div>
              <div class="row">
                <div class="col-12">
                  <label>Photos (max 5)</label>
                  <p>By submitting a public observation you agree to allow Mountain Safety Council to use any of the attached images for the purpose of public safety.</p>
                  <vue-dropzone id="dropzone" :options="{
                    url: '/api/observationphoto',
                    thumbnailWidth: 300,
                    maxFilesize: 20,
                    headers: { 'UUID': formData.uuid },
                    acceptedFiles: 'image/jpeg,image/png,image/gif',
                    maxFiles: 5,
                    dictDefaultMessage: 'Click to select images'
                  }" />
                </div>
              </div>
              <div class="row obsform__advanced mt-4">
                <div class="col-12">
                  <div class="row">
                    <div class="col-12">
                      <h3>Personal information (optional)</h3>
                      <em>Only your name will be visible to the public</em>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12">
                      <label for="formData-submitter-name">Name</label>
                      <input type="text" id="formData-submitter-name" v-model="formData.submitter.name">
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12">
                      <label for="formData-submitter-email">Email</label>
                      <input type="email" id="formData-submitter-email" v-model="formData.submitter.email">
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12">
                      <label for="formData-submitter-phone">Phone</label>
                      <input type="text" id="formData-submitter-phone" v-model="formData.submitter.phone">
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-12">
                  <vue-recaptcha :sitekey="recaptcha" @verify="onRecaptchaVerify" @expired="onRecaptchaExpired" />
                </div>
              </div>
            </section>
          </div>
        </div>
        <div :class="['row obsform__steps', {'first-step': step == 0}]">
          <div class="col-6 text-left">
            <button class="button button--back" @click="prevStep()" v-if="step > 0">Back</button>
          </div>
          <div class="col-6 text-right">
            <button class="button button--next" @click="nextStep()" :disabled="!canNext">{{ nextLabel }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import PublicMarker from '../components/public-marker/PublicMarker';
  import Loader from '../components/loader/Loader';
  import Mapbox from '../components/map/Map';
  import MultiChoice from "../components/form/MultiChoice";
  import { Datetime } from 'vue-datetime';
  import VueScrollTo from 'vue-scrollto';
  import vueSlider from 'vue-slider-component';
  import VueRecaptcha from 'vue-recaptcha';
  import API from '../api/API';
  import { mapState } from 'vuex';
  import vue2Dropzone from 'vue2-dropzone';
  import v4 from 'uuid';
  import 'vue2-dropzone/dist/vue2Dropzone.min.css';
  import constants from "../constants"

  let mapbox = null;
  const handlers = ['scrollZoom', 'boxZoom', 'dragRotate', 'dragPan', 'keyboard', 'doubleClickZoom', 'touchZoomRotate'];

  export default {
    components: {
      PublicMarker,
      Loader,
      MultiChoice,
      Mapbox,
      datetime: Datetime,
      VueScrollTo,
      vueSlider,
      VueRecaptcha,
      vueDropzone: vue2Dropzone,
    },

    filters: {},

    data() {
      return {
        step: 0,
        submitting: false,
        isAdvanced: false,
        maxDatetime: new Date().toISOString(),

        formData: {
          uuid: null,
          type: '',
          name: '',
          time: '',
          location: {},
          avalanche: {
            isWitnessed: null,
            estimatedTime: '',
            measurement: {
              length: null,
              width: null,
              depth: null,
            },
            size: null,
            character: null,
            triggerType: null,
            aspect: null,
            elevation: null,
            slopeAngle: null,
            isPeopleInvolved: null,
            peopleInvolved: {
              activityType: '',
              groupSize: null,
              peopleCaught: null,
              peopleInjured: null,
            },
            isStructuresInvolved: null,
            structuresInvolved: '',
            isPublic: null,
          },
          snowpack: {
            aspect: null,
            elevation: null,
            depth: null,
            surfaceConditions: null,
            footOrSkiPenetration: null,
            footPenetration: null,
            skiPenetration: null,
            testResult: '',
            fractureCharacter: null,
            fractureDepth: null,
          },
          submitter: {
            name: '',
            email: '',
            phone: '',
          },
          comment: '',
          recaptchaResponse: '',
        },

        aspects: ['N','NE','E','SE','S','SW','W','NW'],
      };
    },

    computed: {
      ...mapState({
        cms: (state) => state.cms,
        recaptcha: (state) => state.recaptcha,
      }),

      characterOptions() {
        return this.cms.publicObsOptions.Character.filter(item => item.label.toLowerCase() != 'spring conditions');
      },

      canNext() {
        if (this.submitting) return false;
        let av = this.formData.avalanche;

        switch (this.step) {
          case 0:
            return Object.keys(this.formData.location).length > 0;
          case 1:
            if (this.formData.type === 'avalanche' && (av.isPeopleInvolved === null || av.isStructuresInvolved === null)) {
              return false;
            }
            return (this.formData.type && this.formData.name.length > 0);
          case 2:
            if (this.formData.type === 'avalanche') {
              return this.formData.time.length > 0 && (
                (this.formData.avalanche.isWitnessed === true) ||
                (this.formData.avalanche.isWitnessed === false && this.formData.avalanche.estimatedTime.length > 0)
              );
            } else {
              return this.formData.time.length > 0;
            }
          case 3:
            let pi = this.formData.avalanche.peopleInvolved;
            return (
              (!this.formData.avalanche.isPeopleInvolved || (pi.activityType.length > 0 && pi.groupSize > 0)) &&
              (!this.formData.avalanche.isStructuresInvolved || this.formData.avalanche.structuresInvolved.length > 0)
            );
          case 4:
            return this.formData.recaptchaResponse.length > 0;
          default:
            return false;
        }
      },

      nextLabel() {
        return this.step === 4 ? 'Submit' : 'Next Step';
      },

      triggerTypes() {
        return Object.keys(constants.TRIGGER_TYPES).map(name => ({ value: name, label: constants.TRIGGER_TYPES[name] }));
      },
    },

    mounted() {
      this.formData.uuid = v4();
    },

    methods: {
      nextStep() {
        if (this.canNext) {
          let av = this.formData.avalanche;

          switch (this.step) {
            case 2:
              // only need step 3 if avalanche & people/structures involved
              if (this.formData.type !== 'avalanche' || (!av.isPeopleInvolved && !av.isStructuresInvolved)) {
                this.step = 4;
              } else {
                this.step++;
              }
              break;
            case 4:
              this.submit();
              break;
            default:
              this.step++;
          }
        }

        this.updateMapInteractivity();
        VueScrollTo.scrollTo('.obsform', 0, { easing: 'linear' });
      },

      onRecaptchaVerify: function (response) {
        this.formData.recaptchaResponse = response;
      },

      onRecaptchaExpired: function () {
        this.formData.recaptchaResponse = '';
      },

      prevStep() {
        let av = this.formData.avalanche;

        switch (this.step) {
          case 4:
            if (this.formData.type !== 'avalanche' || (!av.isPeopleInvolved && !av.isStructuresInvolved)) {
              this.step = 2;
            } else {
              this.step--;
            }
            break;
          default:
            this.step--;
        }

        this.updateMapInteractivity();
        VueScrollTo.scrollTo('.obsform', 0, { easing: 'linear' });
      },

      submit() {
        this.submitting = true;
        API
          .submit('observation', this.formData)
          .then((response) => {
            this.submitting = false;
            this.onRecaptchaExpired();

            let added = response.data.added;
            let id = added.id;

            this.$store.dispatch('addObservation', added).then(() => {
              this.$router.push({ name: 'observation', params: { id } });
            });
          })
          .catch((error) => {
            this.submitting = false;
            this.onRecaptchaExpired();
            alert('An error occurred. Please try again.');
          });
      },

      handleMapLoad(map) {
        mapbox = map;
        const geolocateButton = document.querySelector('.mapboxgl-ctrl-geolocate');

        if (geolocateButton) {
          geolocateButton.click();
        }
      },

      updateMapInteractivity() {
        if (this.step === 0) {
          handlers.forEach(function(handler) {
            mapbox[handler].enable();
          });
        } else {
          handlers.forEach(function(handler) {
            mapbox[handler].disable();
          });
        }
      },

      updateObservationLocation(map) {
        if (this.step === 0){
          this.formData.location = map.getCenter();
        }
      },
    },
  };
</script>
